import React, { useState, useRef, useEffect } from 'react';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Search, MapPin, ExternalLink, Navigation } from 'lucide-react';

export const PlacesSearch = ({ onSelect, city, placeholder = "Cerca luogo...", initialValue = '' }) => {
  const [query, setQuery] = useState(initialValue || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  
  // Use refs to track debounce timer and abort controller
  const debounceRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (initialValue && initialValue !== query) {
      setQuery(initialValue);
    }
  }, [initialValue]);

  const searchPlaces = (searchQuery) => {
    // Clear any pending debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    // Abort any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (!searchQuery || searchQuery.length < 3) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    // Debounce the actual API call
    debounceRef.current = setTimeout(async () => {
      try {
        // Create new abort controller for this request
        abortControllerRef.current = new AbortController();
        
        const res = await api.get('/places/search', {
          params: { q: searchQuery, city: city || undefined, limit: 8 },
          signal: abortControllerRef.current.signal
        });
        
        setResults(res.data || []);
      } catch (error) {
        // Ignore abort errors
        if (error.name !== 'AbortError' && error.code !== 'ERR_CANCELED') {
          console.error('Error searching places:', error);
        }
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 350);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setShowResults(true);
    searchPlaces(value);
  };

  const handleSelect = (place) => {
    // Clear any pending searches
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    onSelect({
      name: place.name,
      address: place.address,
      city: place.city,
      lat: place.lat,
      lng: place.lng,
      map_url: place.map_url
    });
    setQuery(place.name);
    setShowResults(false);
    setResults([]);
  };

  const handleBlur = (e) => {
    // Delay hiding results to allow click to register
    setTimeout(() => {
      setShowResults(false);
    }, 200);
  };

  return (
    <div className="relative" data-testid="places-search">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onFocus={() => query.length >= 3 && setShowResults(true)}
          onBlur={handleBlur}
          className="pl-10"
          data-testid="places-search-input"
        />
      </div>

      {showResults && (query.length >= 3 || results.length > 0) && (
        <Card className="absolute top-full left-0 right-0 mt-1 z-50 shadow-lg">
          <ScrollArea className="max-h-64">
            <CardContent className="p-0">
              {loading ? (
                <div className="p-3 space-y-2">
                  {[1, 2, 3].map(i => (
                    <Skeleton key={i} className="h-12" />
                  ))}
                </div>
              ) : results.length === 0 ? (
                <p className="p-3 text-center text-muted-foreground text-sm">
                  {query.length < 3 ? 'Digita almeno 3 caratteri' : 'Nessun risultato trovato'}
                </p>
              ) : (
                <div className="divide-y">
                  {results.map((place, index) => (
                    <button
                      key={place.place_id || `place-${index}`}
                      type="button"
                      className="w-full p-3 text-left hover:bg-muted transition-colors flex items-start gap-3"
                      onClick={() => handleSelect(place)}
                      data-testid={`place-result-${index}`}
                    >
                      <MapPin className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{place.name}</p>
                        <p className="text-sm text-muted-foreground truncate">{place.address}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </ScrollArea>
        </Card>
      )}
    </div>
  );
};

export const PlaceCard = ({ name, address, mapUrl, lat, lng, showActions = true }) => {
  const openGoogleMaps = () => {
    const url = mapUrl || `https://www.google.com/maps?q=${lat},${lng}`;
    window.open(url, '_blank');
  };

  const openDirections = () => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    window.open(url, '_blank');
  };

  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50" data-testid="selected-place-card">
      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
        <MapPin className="h-5 w-5 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium">{name}</p>
        {address && <p className="text-sm text-muted-foreground truncate">{address}</p>}
      </div>
      {showActions && lat && lng && (
        <div className="flex gap-1">
          <Button type="button" size="icon" variant="ghost" onClick={openGoogleMaps} title="Apri in Maps">
            <ExternalLink className="h-4 w-4" />
          </Button>
          <Button type="button" size="icon" variant="ghost" onClick={openDirections} title="Indicazioni">
            <Navigation className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default PlacesSearch;
